
import React, { useState, useEffect } from 'react';
import { getFactsForWeekAndRole } from '../../constants/flashFacts';
import { FlashFact } from '../../types';
import { ChevronRight, ChevronLeft, Bookmark, Sparkles, ExternalLink } from 'lucide-react';

interface FlashCardsProps {
  week: number;
  role?: 'partner' | 'pregnant';
  onViewAll?: () => void;
}

export const FlashCards: React.FC<FlashCardsProps> = ({ week, role = 'pregnant', onViewAll }) => {
  const [facts, setFacts] = useState<FlashFact[]>([]);
  const [index, setIndex] = useState(0);
  const [savedFacts, setSavedFacts] = useState<Set<string>>(new Set());
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  useEffect(() => {
    const weekFacts = getFactsForWeekAndRole(week, role);
    setFacts(weekFacts);
    setIndex(0);
  }, [week, role]);

  const handleNext = () => {
    if (animating || facts.length === 0) return;
    setDirection('right');
    setAnimating(true);
    setTimeout(() => {
      setIndex((prev) => (prev + 1) % facts.length);
      setAnimating(false);
    }, 200);
  };

  const handlePrev = () => {
    if (animating || facts.length === 0) return;
    setDirection('left');
    setAnimating(true);
    setTimeout(() => {
      setIndex((prev) => (prev - 1 + facts.length) % facts.length);
      setAnimating(false);
    }, 200);
  };

  const saveFact = (id: string) => {
    const newSet = new Set(savedFacts);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSavedFacts(newSet);
  };

  if (facts.length === 0) return null;

  const currentFact = facts[index];
  const isSaved = savedFacts.has(currentFact.id);

  // Category colors
  const categoryStyles: Record<string, string> = {
    baby: 'bg-pink-50 text-pink-600',
    mom: 'bg-purple-50 text-purple-600',
    partner: 'bg-blue-50 text-blue-600',
    science: 'bg-emerald-50 text-emerald-600',
  };

  return (
    <div className="relative w-full select-none">
      {/* Header */}
      <div className="flex justify-between items-center mb-3 px-1">
        <button
          onClick={onViewAll}
          className="flex items-center gap-2 group"
        >
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-1.5 rounded-lg shadow-sm">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-sm font-bold text-stone-900 group-hover:text-amber-600 transition-colors">FlashFacts</h3>
          {onViewAll && (
            <ChevronRight size={14} className="text-stone-400 group-hover:text-amber-500 transition-colors" />
          )}
        </button>
        {/* Progress dots */}
        <div className="flex gap-1">
          {facts.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-5 bg-amber-500' : 'w-1.5 bg-stone-200'}`}
            />
          ))}
        </div>
      </div>

      {/* Card */}
      <div
        className={`
          relative bg-white border border-stone-100 rounded-2xl p-5 shadow-sm
          transition-all duration-200 ease-out
          ${animating ? (direction === 'right' ? 'translate-x-1 opacity-90' : '-translate-x-1 opacity-90') : 'translate-x-0 opacity-100'}
        `}
      >
        {/* Top row: emoji + category + bookmark */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{currentFact.emoji}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${categoryStyles[currentFact.category] || 'bg-stone-100 text-stone-600'}`}>
              {currentFact.category}
            </span>
          </div>
          <button
            onClick={() => saveFact(currentFact.id)}
            aria-label={isSaved ? "Remove bookmark" : "Save to bookmarks"}
            className={`p-2 rounded-full transition-all active:scale-90 ${isSaved ? 'text-amber-500 bg-amber-50' : 'text-stone-400 hover:bg-stone-50 hover:text-stone-500'}`}
          >
            <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
          </button>
        </div>

        {/* Fact text */}
        <p className="text-stone-800 font-medium leading-relaxed text-base min-h-[60px]">
          {currentFact.fact}
        </p>

        {/* Source attribution */}
        {currentFact.source && (
          currentFact.learnMoreUrl ? (
            <a
              href={currentFact.learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              <span>Source: {currentFact.source}</span>
              <ExternalLink size={10} />
            </a>
          ) : (
            <p className="mt-2 text-xs text-stone-400">
              Source: {currentFact.source}
            </p>
          )
        )}

        {/* Navigation - inside card */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
          <button
            onClick={handlePrev}
            className="flex items-center gap-1 px-3 py-2 text-stone-400 hover:text-stone-700 hover:bg-stone-50 rounded-lg transition-all active:scale-95"
          >
            <ChevronLeft size={18} />
            <span className="text-xs font-medium">Prev</span>
          </button>

          <span className="text-xs text-stone-400 font-medium">
            {index + 1} / {facts.length}
          </span>

          <button
            onClick={handleNext}
            className="flex items-center gap-1 px-3 py-2 text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-all active:scale-95"
          >
            <span className="text-xs font-medium">Next</span>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
